import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Plus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const StatusPill = ({ status }) => {
    const style =
        status === "submitted"
            ? "chip chip-certified"
            : status === "approved"
                ? "chip chip-certified"
                : "chip chip-draft";
    const label = status === "submitted"
        ? "Submitted"
        : status === "approved"
            ? "Approved"
            : "Draft";
    return <span className={style} data-testid="timesheet-status-pill">{label}</span>;
};

const relTime = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const diff = (Date.now() - d.getTime()) / 1000;
        if (diff < 60) return "just now";
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
        return d.toLocaleDateString();
    } catch { return iso; }
};

export default function TimesheetsList() {
    const [rows, setRows] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const nav = useNavigate();

    const load = async () => {
        try {
            const { data } = await api.get("/timesheets");
            setRows(data);
        } catch (err) {
            setError(formatApiError(err));
            setRows([]);
        }
    };
    useEffect(() => { load(); }, []);

    const createDraft = async () => {
        setBusy(true); setError("");
        try {
            const { data } = await api.post("/timesheets", {});
            nav(`/timesheets/${data.id}/edit`);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                <div>
                    <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900" data-testid="timesheets-title">
                        Timesheets
                    </h2>
                    <p className="text-[14px] text-slate-600 mt-1">
                        Weekly hours by job. Draft first, submit when the week's closed.
                    </p>
                </div>
                <button
                    onClick={createDraft}
                    disabled={busy}
                    className="btn btn-primary"
                    data-testid="timesheets-new-button"
                >
                    <Plus size={16} strokeWidth={2.5} />
                    New timesheet
                </button>
            </div>

            {error && (
                <div className="err rounded-[10px] border border-red-200 bg-red-50/70 px-3 py-2 mb-4" data-testid="timesheets-error">
                    {error}
                </div>
            )}

            {rows === null && (
                <div className="glass-card text-[13px] text-slate-500">Loading…</div>
            )}

            {rows && rows.length === 0 && (
                <div className="glass-card" data-testid="timesheets-empty-state">
                    <div className="flex flex-col items-start gap-4">
                        <div className="text-[13px] text-slate-500">Nothing here yet</div>
                        <h3 className="text-2xl font-semibold text-slate-900">
                            No timesheets yet — start your first one.
                        </h3>
                        <p className="text-[14px] text-slate-600 max-w-md">
                            A timesheet covers Monday through Sunday. Fill in the days as they happen; submit at the end of the week.
                        </p>
                        <button
                            onClick={createDraft}
                            disabled={busy}
                            className="btn btn-primary"
                            data-testid="timesheets-empty-new-button"
                        >
                            <Plus size={16} strokeWidth={2.5} />
                            Create the first timesheet
                            <ArrowRight size={16} strokeWidth={2} />
                        </button>
                    </div>
                </div>
            )}

            {rows && rows.length > 0 && (
                <>
                    <div className="mobile-only flex flex-col gap-3" data-testid="timesheets-mobile-list">
                        {rows.map((r) => {
                            const to = r.status === "draft"
                                ? `/timesheets/${r.id}/edit`
                                : `/timesheets/${r.id}`;
                            return (
                                <Link
                                    key={r.id}
                                    to={to}
                                    className="glass-card !py-4 !px-4 block active:opacity-80"
                                    data-testid={`timesheet-card-${r.id}`}
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0 flex-1">
                                            <div className="text-[17px] font-semibold text-slate-900 truncate">
                                                Week of {r.week_ending || "—"}
                                            </div>
                                            <div className="text-[12px] text-slate-500 mt-0.5 truncate">
                                                {r.employee_name || "Unnamed"}
                                            </div>
                                            <div className="mt-2"><StatusPill status={r.status} /></div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <div className="text-[11px] text-slate-400 uppercase tracking-wider">Total hrs</div>
                                            <div className="text-[15px] font-medium tabular-nums text-slate-900">
                                                {r.total_hours == null ? "—" : r.total_hours}
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>

                    <div className="glass-card !p-0 overflow-hidden desktop-only">
                        <div className="overflow-x-auto">
                            <table className="g-table" data-testid="timesheets-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: "20%" }}>Week ending</th>
                                        <th style={{ width: "24%" }}>Employee</th>
                                        <th style={{ width: "14%" }}>Total hours</th>
                                        <th style={{ width: "12%" }}>Status</th>
                                        <th style={{ width: "16%" }}>Updated</th>
                                        <th style={{ width: "14%" }} className="text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r) => (
                                        <tr key={r.id} data-testid={`timesheet-row-${r.id}`}>
                                            <td className="tabular-nums font-medium">{r.week_ending || <span className="text-slate-400">—</span>}</td>
                                            <td>{r.employee_name || <span className="text-slate-400">Unnamed</span>}</td>
                                            <td className="tabular-nums">{r.total_hours == null ? <span className="text-slate-400">—</span> : r.total_hours}</td>
                                            <td><StatusPill status={r.status} /></td>
                                            <td className="text-slate-500 text-[13px]">{relTime(r.updated_at)}</td>
                                            <td>
                                                <div className="flex gap-2 justify-end">
                                                    <Link
                                                        to={r.status === "draft" ? `/timesheets/${r.id}/edit` : `/timesheets/${r.id}`}
                                                        className="btn btn-secondary btn-sm"
                                                        data-testid={`timesheet-open-${r.id}`}
                                                    >
                                                        {r.status === "draft" ? "Edit" : "View"}
                                                    </Link>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}
